import type { ReactElement } from "react";
import type { LamaMaturityProfileOutput } from "@playbookai/widget-schemas";
import { MaturityRadar } from "../MaturityRadar/MaturityRadar.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
  lamaScorePercentLabel,
  lamaScoreToPercent,
} from "../lama-report-presentation.js";

/**
 * LAMA Maturity Profile — the report's pillar radar with target/previous
 * overlays. The under-three-pillars state is represented by the DATA (fewer
 * than three rows) and rendered as a bounded fallback, mirroring the old
 * report page's `radarData.length >= 3` gate.
 *
 * Suppression is checked FIRST and worded differently: an anonymous wave below
 * the min-N floor has no pillars for a reason that is not "not enough pillars
 * were scored".
 */
export function LamaMaturityProfileWidget({
  data,
}: WidgetBodyProps<LamaMaturityProfileOutput>): ReactElement {
  if (data.suppressed) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  // Conditional spreads, not `?? undefined`: exactOptionalPropertyTypes means
  // the optional axis props cannot accept null.
  // Percent at display (D8): the dataset carries 0–5, the radar draws 0–100.
  const radarData = data.pillars.map((row) => ({
    label: row.pillar,
    value: lamaScoreToPercent(row.current),
    ...(row.target != null ? { target: lamaScoreToPercent(row.target) } : {}),
    ...(row.previous != null
      ? { previous: lamaScoreToPercent(row.previous) }
      : {}),
  }));

  if (radarData.length < 3) {
    return (
      <p className="text-sm text-pb-text-muted">
        Not enough scored pillars yet — a maturity profile needs at least three
        scored pillars.
      </p>
    );
  }

  const hasTargets = data.pillars.some((row) => row.target != null);
  const hasPrevious = data.pillars.some((row) => row.previous != null);
  // Compose the overlay clauses so every state reads correctly — concatenating
  // each one drops the ", with" when only `previous` is present.
  const overlays = [
    hasTargets ? "a target overlay" : null,
    hasPrevious ? "a previous-wave overlay" : null,
  ].filter((clause): clause is string => clause !== null);

  return (
    <div>
      {/* What this section IS, in one line. The report also carries the v3
          Maturity Overview radar, and the two answer different questions —
          that one compares the latest wave with the facilitator BASELINE, this
          one with the PREVIOUS WAVE — so saying which is which is what stops
          the pair reading as the same chart twice. */}
      <p className="text-xs text-pb-text-muted">
        Pillar scores for the latest wave
        {hasPrevious ? ", with the previous wave as the dotted overlay" : ""}.
      </p>
      <div
        className="mt-2 overflow-x-auto overscroll-x-contain rounded-md pb-2 focus-ring"
        /* A horizontally scrollable region MUST be keyboard focusable or a
           keyboard-only user cannot reach the off-screen part of the radar
           (WCAG 2.1.1). The named region + focus ring are the sanctioned
           pattern and preserve the shipped LamaReportPage behaviour. */
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label="Scrollable maturity profile chart"
      >
        <MaturityRadar
          data={radarData}
          max={100}
          unit="%"
          height={420}
          className="mx-auto min-w-150 max-w-3xl"
          previousLabel="Previous wave"
          ariaLabel={`Maturity profile — ${radarData.length} pillars on a percent scale${
            overlays.length > 0 ? `, with ${overlays.join(" and ")}` : ""
          }.`}
        />
      </div>
      {/*
        THE NON-POINTER EQUIVALENT. The radar's own tooltip is pointer-only by
        construction — recharts renders it inside the `aria-hidden` chart
        subtree with `tabIndex={-1}` and no role, and its `accessibilityLayer`
        is inert on a centric chart — so every figure it reveals has to be
        readable some other way. The hierarchy section pairs its radar with a
        visible table; this section has only the chart, so the equivalent is
        `sr-only` and costs no vertical space.
      */}
      {/* eslint-disable-next-line ds/no-hand-built -- kept: an sr-only table, the radar's non-pointer equivalent; it has no visible look for DataTable to take. */}
      <table className="sr-only">
        {/* Deliberately NOT the visible line above — a caption that repeats
            the paragraph verbatim is announced twice and tells a reader
            nothing new about the table. */}
        <caption>Every pillar figure this radar draws</caption>
        <thead>
          <tr>
            <th scope="col">Pillar</th>
            <th scope="col">Latest</th>
            {hasPrevious ? <th scope="col">Previous wave</th> : null}
            {/* Mirrors the radar's own overlays: a column that describes a
                shape nobody drew would be as misleading as a missing one. */}
            {hasTargets ? <th scope="col">Target</th> : null}
          </tr>
        </thead>
        <tbody>
          {/* Keyed by POSITION, not by label: a pillar label carries no
              uniqueness constraint and the rows are sorted by it. */}
          {data.pillars.map((row, index) => (
            <tr key={`${String(index)}-${row.pillar}`}>
              <th scope="row">{row.pillar}</th>
              <td>{lamaScorePercentLabel(row.current)}</td>
              {hasPrevious ? (
                <td>
                  {row.previous == null
                    ? "Not measured"
                    : lamaScorePercentLabel(row.previous)}
                </td>
              ) : null}
              {hasTargets ? (
                <td>
                  {row.target == null
                    ? "No target"
                    : lamaScorePercentLabel(row.target)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
